import { CssBaseline, ThemeProvider } from '@mui/material';
import { Loader, theme, Toast } from '@watter/shared-components';
import '@watter/shared-components/src/styles';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LandingPage } from './pages/landing-page';

import { NoInternetPopUp } from './components/noInternetPopup/NoInternetPopup';
import { StaticData } from './config/enum/staticData';
import { environment } from './environments/environment';
import SocialLoginAuthCallback from './hooks/social-login-callback';
import { NotificationSetup } from './pages/push-notifications-setup';
import { forceSignOutUser } from './service/apolloClient';
import { fetchDashboardEarnings, fetchDashboardMetrics, fetchStaticData } from './services/dashboard-service';
import { RootState } from './store';
import { forceHideLoader, hideLoader, setSession, showLoader } from './store/reducers/appSlice';
import { setEarningsData, setIsMetricsAPICalled, setMetricsData } from './store/reducers/heaterSlice';
import { setUnreadNotificationsCount } from './store/reducers/notificationsSlice';
import { updateStaticStateData } from './store/reducers/staticDataSlice';
import { openToast, resetToast } from './store/reducers/toastSlice';
import { updateLoginStatus } from './store/reducers/userSlice';
import './translations/i18n';
import { useNotificationSocket } from './web-socket/notifications-use-socket';

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolClientId: environment.USER_POOL_CLIENT_ID || '',
            userPoolId: environment.USER_POOL_ID || '',
            identityPoolId: environment.IDENTITY_POOL_ID || '',
            loginWith: {
                oauth: {
                    domain: environment.OAUTH_DOMAIN || '',
                    scopes: ['email', 'openid', 'profile'],
                    redirectSignIn: (environment.REDIRECT_SIGN_IN_URLS || '')
                        .split(',') // Split the string into an array
                        .map(url => url.trim()) // Remove extra spaces
                        .filter(Boolean), // Remove empty strings
                    redirectSignOut: [environment.REDIRECT_SIGN_OUT_URLS || ''],
                    responseType: 'code', // Use 'token' for implicit flow
                },
            },
        },
    },
    Storage: {
        S3: {
            bucket: environment.S3_BUCKET,
            region: environment.APP_REGION,
        },
    },
});
const App: React.FC = () => {
    const showToast = useSelector((state: RootState) => state.toast);
    const dispatch = useDispatch();
    SocialLoginAuthCallback();
    const onNotificationCount = () => {
        dispatch(setUnreadNotificationsCount(1));
    };
    useNotificationSocket(onNotificationCount);
    const { t } = useTranslation();
    React.useEffect(() => {
        const storedVersion = localStorage.getItem('APP_VERSION');
        if (storedVersion && storedVersion !== environment.APP_VERSION) {
            localStorage.setItem('APP_VERSION', environment.APP_VERSION);
            window.location.reload();
        } else if (!storedVersion) {
            localStorage.setItem('APP_VERSION', environment.APP_VERSION);
        }
    }, []);

    const hasRunForceHideLoader = React.useRef(false);
    React.useEffect(() => {
        if (!hasRunForceHideLoader.current) {
            console.warn('triggered on hard reload');
            dispatch(forceHideLoader());
            hasRunForceHideLoader.current = true;
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (localStorage.getItem('reduxVersionChanged') === 'true') {
            dispatch(
                openToast({
                    show: true,
                    message: t('user_session_expired_error'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
            forceSignOutUser();
            localStorage.removeItem('reduxVersionChanged');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = React.useCallback(async () => {
        dispatch(setIsMetricsAPICalled(false));
        const earnings = await fetchDashboardEarnings();
        const metrics = await fetchDashboardMetrics(true);
        const staticData = await fetchStaticData();

        if (earnings) {
            dispatch(
                setEarningsData({
                    gpu_earning_month: earnings?.gpu_earning_month,
                    storage_earning_month: earnings?.storage_earning_month,
                    gpu_earning_total: earnings?.gpu_earning_total,
                    storage_earning_total: earnings?.storage_earning_total,
                    machine_name: earnings?.machine_name,
                    gpu_earnings_updated_at: earnings?.gpu_earnings_updated_at,
                }),
            );
        }
        if (metrics) {
            dispatch(setIsMetricsAPICalled(true));
            dispatch(
                setMetricsData({
                    power_sensor_timestamp: metrics?.sensor_timestamp,
                    water_sensor_timestamp: metrics?.sensor_timestamp,
                    avg_gpu_util_7d: metrics?.avg_gpu_util_7d,
                    avg_gpu_util_30d: metrics?.avg_gpu_util_30d,
                    power_usage: metrics?.power_usage,
                    water_temperature: metrics?.water_temperature,
                    temperature_history: metrics?.temperature_history,
                    power: metrics?.power,
                    internet: metrics?.internet,
                    machine_id: metrics?.device_id,
                    heatingStatus: metrics?.heating_status,
                    computingStatus: metrics?.computing_status,
                }),
            );
        }
        if (staticData) {
            const tempStaticData = staticData?.[StaticData.TEMPERATURE];
            if (tempStaticData) {
                const payload = {
                    minValue:
                        tempStaticData?.[0] && tempStaticData?.[0]?.key ? parseInt(tempStaticData?.[0]?.key, 10) : 105,
                    maxValue:
                        tempStaticData?.[1] && tempStaticData?.[1]?.key ? parseInt(tempStaticData?.[1]?.key, 10) : 135,
                };
                dispatch(
                    updateStaticStateData({
                        key: StaticData.TEMPERATURE,
                        value: payload,
                    }),
                );
            }
        }
    }, [dispatch]);
    const fetchCurrentSession = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const session = await fetchAuthSession({ forceRefresh: true });
            if (session) {
                dispatch(
                    setSession({
                        token: session?.tokens?.idToken?.toString(),
                        payload: session?.tokens?.idToken?.payload,
                    }),
                );
                fetchData();
                dispatch(updateLoginStatus(true));
            }
        } catch (err) {
            console.error('err==>', err);
            dispatch(hideLoader());
            dispatch(updateLoginStatus(false));
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, fetchData]);
    // Memoize getUser to avoid missing dependency warning in useEffect
    const getUser = React.useCallback(async () => {
        try {
            const currentUser = await getCurrentUser();
            if (currentUser?.userId || currentUser?.username) {
                await fetchCurrentSession();
            }
        } catch (err: any) {
            console.error(err, 'in catch of getUser()');
        }
    }, [fetchCurrentSession]);
    React.useEffect(() => {
        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            if (payload.event === 'signInWithRedirect') {
                getUser();
            }
        });
        return unsubscribe;
    }, [getUser]);
    const closeToast = () => {
        dispatch(resetToast({ show: false, message: null, isAlertOpen: false }));
    };
    const { activeHTTPRequests } = useSelector((state: any) => state.app);

    if ('serviceWorker' in navigator && environment.NODE_ENV !== 'test') {
        console.warn(' inside serviceWorker in navigator');
        navigator.serviceWorker.ready.then(registration => {
            if (!navigator.serviceWorker.controller) {
                // No active controller means no active service worker
                return navigator.serviceWorker.register('/firebase-messaging-sw.js');
            }
            console.warn('Service Worker already active:', registration);
            return registration; // Already registered and active
        });
    }
    if (typeof window !== 'undefined' && window.Cypress) {
        (window as any).testHooks = {
            fetchData,
            fetchCurrentSession,
            getUser,
            onNotificationCount,
        };
    }
    React.useEffect(() => {
        if (activeHTTPRequests > 0) {
            const timeout = setTimeout(() => {
                dispatch(forceHideLoader());
            }, 30000);
            return () => {
                clearTimeout(timeout);
            };
        }
        return undefined;
    }, [activeHTTPRequests, dispatch]);
    return (
        <div className="app">
            {activeHTTPRequests >= 1 && environment.NODE_ENV !== 'test' && <Loader />}
            <Toast
                open={showToast?.show}
                message={showToast?.message}
                severity={showToast?.severity}
                handleClose={closeToast}
            />
            <NotificationSetup />
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <LandingPage />
                <NoInternetPopUp />
            </ThemeProvider>
        </div>
    );
};

export { App };
